import { useState } from "react";
import { Button } from "@datapay-by-datacom/design-system";
import type { CompanyConfigVersion } from "../../generated/core-api";
import { ProfileSummary } from "./ProfileSummary";
import s from "./VersionRow.module.css";

/** Human-readable timestamp; falls back to the raw value if unparseable. */
function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

interface VersionRowProps {
  version: CompanyConfigVersion;
  onRetire: () => void;
  presentation?: "legacy" | "redesign";
}

/**
 * A single row in the version history list. Shows the version number, status
 * badge, and publish/retire metadata; expands in place to reveal the
 * read-only {@link ProfileSummary} for that version. Published versions can
 * be retired from here.
 */
export function VersionRow({
  version,
  onRetire,
  presentation = "redesign",
}: VersionRowProps) {
  const [expanded, setExpanded] = useState(false);
  const isRetired = version.status === "RETIRED";
  const isRedesign = presentation === "redesign";
  const detailsId = `version-${version.config_code}-${version.version}-details`.replace(
    /[^a-zA-Z0-9_-]/g,
    "-"
  );

  return (
    <li
      className={`${s.item} ${isRedesign ? s.redesignItem : ""} ${
        isRedesign && !isRetired ? s.redesignItemPublished : ""
      }`}
    >
      <div className={s.itemHead}>
        <button
          type="button"
          className={s.toggle}
          aria-expanded={expanded}
          aria-controls={detailsId}
          aria-label={`${expanded ? "Hide" : "Show"} version ${version.version} details`}
          onClick={() => setExpanded((open) => !open)}
        >
          <span className={s.chevron} aria-hidden="true">
            {expanded ? "▾" : "▸"}
          </span>
          <span className={s.version}>
            {isRedesign ? `Version ${version.version}` : `v${version.version}`}
          </span>
          <span
            className={`${s.badge} ${isRetired ? s.badgeRetired : s.badgePublished}`}
          >
            {version.status}
          </span>
          {version.based_on != null && (
            <span className={s.basedOn}>based on v{version.based_on}</span>
          )}
        </button>
        {!isRetired && (
          <Button variant="tertiary" onPress={onRetire}>
            Retire…
          </Button>
        )}
      </div>

      <p className={s.reason}>
        {isRedesign && <span className={s.reasonLabel}>Change reason</span>}
        <span>{version.change_description}</span>
      </p>

      <p className={s.meta}>
        {isRedesign
          ? `Published ${formatTimestamp(version.published_at)}`
          : `Published by ${version.published_by} · ${formatTimestamp(
              version.published_at
            )}`}
      </p>

      {isRetired && (
        <p className={s.meta}>
          Retired
          {!isRedesign && version.retired_by
            ? ` by ${version.retired_by}`
            : ""}
          {version.retired_at ? ` · ${formatTimestamp(version.retired_at)}` : ""}
          {version.retire_description ? ` — ${version.retire_description}` : ""}
        </p>
      )}

      {expanded && (
        <div
          id={detailsId}
          className={isRedesign ? s.expandedDetails : undefined}
        >
          <ProfileSummary
            profile={version.profile}
            presentation={isRedesign ? "review" : "compact"}
          />
        </div>
      )}
    </li>
  );
}
